import { useState, useCallback, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { searchMerchants } from '../api/merchants'
import { getLocation } from '../utils/geolocation'
import Spinner from '../components/common/Spinner'
import type { MerchantSearchResult } from '../types'

const PAGE_SIZE = 20

const SHOP_CATEGORIES = [
  'Electronics',
  'Clothing & Apparel',
  'Food & Groceries',
  'Home & Furniture',
  'Beauty & Health',
  'Sports & Outdoors',
  'Books & Stationery',
  'Toys & Kids',
  'Automotive',
  'Other',
] as const

function distanceLabel(km: number | undefined | null): string | null {
  if (km == null) return null
  if (km < 1) return `${Math.round(km * 1000)}m`
  return `${km.toFixed(1)}km`
}

function ShopAvatar({ logoUrl }: { logoUrl?: string | null }) {
  const [imgError, setImgError] = useState(false)

  if (logoUrl && !imgError) {
    return (
      <img
        src={logoUrl}
        alt=""
        className="w-12 h-12 rounded-xl object-cover flex-shrink-0"
        onError={() => setImgError(true)}
      />
    )
  }
  return (
    <div
      className="w-12 h-12 rounded-xl flex items-center justify-center text-xl flex-shrink-0"
      style={{ background: 'var(--app-page-bg)' }}
    >
      🏪
    </div>
  )
}

export default function ShopsListPage() {
  const { t } = useTranslation('worker')
  const navigate = useNavigate()
  const [query, setQuery] = useState('')
  const [activeCategory, setActiveCategory] = useState<string | null>(null)
  const [items, setItems] = useState<MerchantSearchResult[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [geoLat, setGeoLat] = useState<number | null>(null)
  const [geoLng, setGeoLng] = useState<number | null>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const load = useCallback(async (q: string, skip: number, append: boolean, lat?: number, lng?: number, cat?: string | null) => {
    if (skip === 0) setLoading(true)
    else setLoadingMore(true)
    try {
      const res = await searchMerchants(q, skip, PAGE_SIZE, lat ?? undefined, lng ?? undefined, cat ?? undefined)
      setTotal(res.total)
      setItems((prev) => (append ? [...prev, ...res.items] : res.items))
    } catch {
      // keep existing items on error
    } finally {
      setLoading(false)
      setLoadingMore(false)
    }
  }, [])

  // Initial load: alphabetical first, then geo if available
  useEffect(() => {
    load('', 0, false)

    getLocation()
      .then(({ latitude, longitude }) => {
        setGeoLat(latitude)
        setGeoLng(longitude)
        load('', 0, false, latitude, longitude, activeCategory)
      })
      .catch(() => {
        // GPS unavailable — keep alphabetical
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      load(query, 0, false, geoLat ?? undefined, geoLng ?? undefined, activeCategory)
    }, 300)
  }, [query, activeCategory, geoLat, geoLng, load])

  const hasMore = items.length < total
  const hasGeo = geoLat != null && geoLng != null

  const sectionLabel = query
    ? t('shops.shopsMatching', { count: total, query })
    : hasGeo
    ? t('shops.nearby')
    : t('shops.shopsCount', { count: total })

  return (
    <div className="min-h-screen safe-area-bottom" style={{ background: 'var(--app-page-bg)' }}>
      {/* Header */}
      <div className="px-4 pt-4 pb-2">
        <div className="flex items-center gap-3 mb-3">
          <button
            className="text-sm opacity-60"
            style={{ color: 'var(--tg-theme-link-color)' }}
            onClick={() => navigate(-1)}
          >
            ← Back
          </button>
          <h1 className="text-xl font-bold flex-1" style={{ color: 'var(--tg-theme-text-color)' }}>
            {t('shops.title')}
          </h1>
          <button
            className="text-sm font-medium px-3 py-1.5 rounded-xl"
            style={{ background: 'var(--tg-theme-button-color)', color: 'var(--tg-theme-button-text-color)' }}
            onClick={() => navigate('/shops/new')}
          >
            {t('shops.add')}
          </button>
        </div>

        {/* Search */}
        <div className="flex items-center gap-2 rounded-xl px-3" style={{ background: 'var(--app-card-bg)' }}>
          <span className="opacity-40">🔍</span>
          <input
            style={{
              background: 'transparent',
              color: 'var(--tg-theme-text-color)',
              border: 'none',
              padding: '10px 0',
              width: '100%',
              fontSize: '15px',
              outline: 'none',
            }}
            placeholder={t('shops.searchPlaceholder')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          {query && (
            <button className="text-xl leading-none opacity-40" style={{ color: 'var(--tg-theme-text-color)' }} onClick={() => setQuery('')}>
              ×
            </button>
          )}
        </div>
      </div>

      {/* Category chips */}
      <div className="overflow-x-auto pb-2 pt-1">
        <div className="flex gap-2 px-4" style={{ width: 'max-content' }}>
          <button
            className="px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap flex-shrink-0"
            style={{
              background: activeCategory == null ? 'var(--tg-theme-button-color)' : 'var(--app-card-bg)',
              color: activeCategory == null ? 'var(--tg-theme-button-text-color)' : 'var(--tg-theme-text-color)',
            }}
            onClick={() => setActiveCategory(null)}
          >
            {t('common:all', { ns: 'common' })}
          </button>
          {SHOP_CATEGORIES.map((cat) => (
            <button
              key={cat}
              className="px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap flex-shrink-0"
              style={{
                background: activeCategory === cat ? 'var(--tg-theme-button-color)' : 'var(--app-card-bg)',
                color: activeCategory === cat ? 'var(--tg-theme-button-text-color)' : 'var(--tg-theme-text-color)',
              }}
              onClick={() => setActiveCategory(activeCategory === cat ? null : cat)}
            >
              {t(`shops.categories.${cat}`)}
            </button>
          ))}
        </div>
      </div>

      {/* Section label */}
      {!loading && (
        <p className="px-4 pb-2 text-xs opacity-40" style={{ color: 'var(--tg-theme-text-color)' }}>
          {sectionLabel}
        </p>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-16">
          <Spinner />
        </div>
      )}

      {!loading && items.length === 0 && (
        <div className="flex flex-col items-center py-16 gap-3">
          <div className="text-5xl">🏪</div>
          <p className="text-sm opacity-40" style={{ color: 'var(--tg-theme-text-color)' }}>
            {query ? t('shops.noShopsFound') : t('shops.noShops')}
          </p>
          {!query && (
            <button
              className="text-sm font-medium mt-1"
              style={{ color: 'var(--tg-theme-link-color)' }}
              onClick={() => navigate('/shops/new')}
            >
              {t('shops.registerShop')}
            </button>
          )}
        </div>
      )}

      {/* Shop list */}
      <div className="px-4 space-y-2 pb-8">
        {items.map((m) => {
          const dist = distanceLabel(m.distance_km)
          return (
            <button
              key={m.id}
              className="w-full text-left rounded-2xl p-3 active:opacity-70"
              style={{ background: 'var(--app-card-bg)' }}
              onClick={() => navigate(`/shops/${m.id}`)}
            >
              <div className="flex items-center gap-3">
                <ShopAvatar logoUrl={m.logo_url} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <p className="text-sm font-semibold truncate" style={{ color: 'var(--tg-theme-text-color)' }}>
                      {m.business_name}
                    </p>
                    {m.is_crowd_verified && (
                      <span className="text-xs flex-shrink-0 font-medium" style={{ color: '#22c55e' }}>✓</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                    {(m.address || m.location) && (
                      <p className="text-xs opacity-50 truncate" style={{ color: 'var(--tg-theme-text-color)' }}>
                        📍 {m.address || m.location}
                      </p>
                    )}
                    {m.shop_category && (
                      <span
                        className="text-xs px-1.5 py-0.5 rounded-md flex-shrink-0"
                        style={{ background: 'var(--app-page-bg)', color: 'var(--tg-theme-text-color)', opacity: 0.6 }}
                      >
                        {m.shop_category}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-0.5">
                    {(m.product_count ?? 0) > 0 && (
                      <p className="text-xs opacity-40" style={{ color: 'var(--tg-theme-text-color)' }}>
                        {t('shops.productsCount', { count: m.product_count ?? 0 })}
                      </p>
                    )}
                    {dist && (
                      <p className="text-xs font-medium" style={{ color: 'var(--tg-theme-link-color)' }}>
                        {t('shops.away', { dist })}
                      </p>
                    )}
                  </div>
                  {!dist && (
                    <p className="text-xs mt-0.5 opacity-40" style={{ color: 'var(--tg-theme-link-color)' }}>
                      {t('shops.tapToAdd')}
                    </p>
                  )}
                </div>
              </div>
            </button>
          )
        })}
      </div>

      {/* Load more */}
      {hasMore && (
        <div className="px-4 pb-8">
          <button
            className="w-full py-2.5 rounded-xl text-sm font-medium disabled:opacity-40"
            style={{ background: 'var(--app-card-bg)', color: 'var(--tg-theme-link-color)' }}
            disabled={loadingMore}
            onClick={() => load(query, items.length, true, geoLat ?? undefined, geoLng ?? undefined, activeCategory)}
          >
            {loadingMore ? t('common:loading_', { ns: 'common' }) : t('common:loadMore', { ns: 'common', count: total - items.length })}
          </button>
        </div>
      )}
    </div>
  )
}
